import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  X, 
  Copy, 
  Trash2, 
  Download, 
  Play, 
  Pause,
  AlertTriangle,
  CheckCircle,
  Activity,
  Zap,
  Settings,
  RefreshCw,
  Search
} from 'lucide-react';
import { useAudioContext } from '@/contexts/AudioContext';
import { useConsoleLogCapture } from '@/hooks/useConsoleLogCapture';
import { audioPerformanceLogger } from '@/utils/audioPerformanceLogger';
import { getAudioDiagnosticsReport, resetAudioDiagnostics } from '@/utils/audioContextDiagnostics';
import { correlateWithUltraLowAudio, getBufferMonitoringReport, resetBufferMonitoring, stopBufferMonitoring } from '@/utils/audioBufferMonitor';
import { bufferOptimizer } from '@/utils/bufferOptimizer';
import { isDashboardAccessible } from '@/utils/environment';

export function AudioPerformanceDashboard({ isOpen, onClose }) {
  // Security check: Only allow dashboard in development/test builds
  if (!isDashboardAccessible()) {
    console.warn('AudioPerformanceDashboard: Access denied in production build');
    return null;
  }

  const { audioRef, analyserRef, dataArrayRef, currentSong, isPlaying } = useAudioContext();
  
  // Performance metrics state
  const [metrics, setMetrics] = useState({
    audioLatency: 0,
    bufferHealth: 'good',
    audioContextState: 'unknown',
    sampleRate: 0,
    bufferSize: 0,
    dropoutCount: 0,
    majorDropoutCount: 0, // Count of severe dropouts
    lastDropoutTime: null,
    lastMajorDropoutTime: null, // Track severe dropouts
    uptime: 0,
    audioLevelRange: { current: 0, avg: 0, min: 255, max: 0 }, // Real-time audio levels
    sessionStability: 'stable', // Track session stability
    memoryPressure: 'normal' // Memory pressure indicator
  });

  // Real-time log state
  const [logs, setLogs] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isInitialized, setIsInitialized] = useState(true); // Start as initialized
  const [diagnosticsReport, setDiagnosticsReport] = useState(null); // Audio context diagnostics
  const [bufferReport, setBufferReport] = useState(null); // Buffer monitoring report
  const [bufferOptimizerStatus, setBufferOptimizerStatus] = useState(null); // Buffer optimizer status
  const logsContainerRef = useRef(null);
  const metricsIntervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  
  // Track changes to detect transitions
  const lastSongRef = useRef(null);
  const trackTransitionTimeRef = useRef(null);
  const lastPlayingStateRef = useRef(isPlaying);

  // Add log entry
  const addLog = useCallback((level, category, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      level, // 'info', 'warn', 'error', 'debug'
      category, // 'audio', 'system', 'performance', 'visualizer'
      message,
      data
    };
    
    setLogs(prev => {
      const newLogs = [...prev, logEntry];
      // Keep only last 500 logs to prevent memory issues
      return newLogs.slice(-500);
    });
  }, []);

  // Capture console logs when dashboard is open AND playing
  useConsoleLogCapture(isOpen && isPlaying ? addLog : null);

  // Register with global performance logger when dashboard is open AND playing
  useEffect(() => {
    if (isOpen && isPlaying) {
      audioPerformanceLogger.registerHandler(addLog);
      addLog('info', 'system', 'Audio Performance Dashboard logging started');
      
      return () => {
        audioPerformanceLogger.unregisterHandler(addLog);
      };
    } else if (isOpen && !isPlaying) {
      // Dashboard open but paused - stop logging
      audioPerformanceLogger.unregisterHandler(addLog);
    }
  }, [isOpen, isPlaying, addLog]);

  // Track song changes and pause/play transitions
  useEffect(() => {
    // Detect track change
    if (currentSong && lastSongRef.current && currentSong.id !== lastSongRef.current.id) {
      trackTransitionTimeRef.current = Date.now();
      addLog('info', 'audio', `Track changed: ${lastSongRef.current.name || 'Unknown'} → ${currentSong.name || 'Unknown'}`);
    }
    lastSongRef.current = currentSong;
    
    // Detect play/pause state changes
    if (isPlaying !== lastPlayingStateRef.current) {
      if (isPlaying) {
        addLog('info', 'audio', 'Playback resumed - logging activated');
      } else {
        addLog('info', 'audio', 'Playback paused - logging suspended');
      }
    }
    lastPlayingStateRef.current = isPlaying;
  }, [currentSong, isPlaying, addLog]);

  // Initialize dashboard
  useEffect(() => {
    if (isOpen) {
      addLog('info', 'system', 'Dashboard opened, starting initialization...');
      startTimeRef.current = Date.now();
      addLog('info', 'system', 'Audio Performance Dashboard initialized successfully');
    }
  }, [isOpen, addLog]);

  // Performance monitoring with memory pressure detection
  useEffect(() => {
    if (!isOpen) return;

    const updateMetrics = () => {
      setMetrics(prevMetrics => {
        const newMetrics = { ...prevMetrics };
        
        // Calculate uptime
        newMetrics.uptime = Math.floor((Date.now() - startTimeRef.current) / 1000);
        
        // Update audio context diagnostics every 5 seconds
        if (newMetrics.uptime % 5 === 0) {
          const report = getAudioDiagnosticsReport();
          setDiagnosticsReport(report);
          
          // Update buffer monitoring report
          const bufferReport = getBufferMonitoringReport();
          setBufferReport(bufferReport);
          
          // Update buffer optimizer status
          const bufferOptimizerStatus = bufferOptimizer.getBufferStatus();
          setBufferOptimizerStatus(bufferOptimizerStatus);
          
          // Log diagnostic issues
          if (report.potentialIssues && report.potentialIssues.length > 0) {
            report.potentialIssues.forEach(issue => {
              addLog('warn', 'diagnostics', `🔍 ${issue.type}: ${issue.description}`, issue);
            });
          }
          
          // Log buffer issues
          if (bufferReport.isMonitoring && bufferReport.underrunDetection.isInUnderrun) {
            addLog('error', 'buffer', '🔴 Active buffer underrun detected', {
              bufferHealth: bufferReport.bufferHealth,
              statistics: bufferReport.statistics
            });
          }
        }
        
        // Monitor memory pressure in Electron
        const isElectron = typeof window !== 'undefined' && window.electronAPI;
        if (isElectron && window.performance && window.performance.memory) {
          const memInfo = window.performance.memory;
          const heapUsed = memInfo.usedJSHeapSize;
          const heapTotal = memInfo.totalJSHeapSize;
          const heapLimit = memInfo.jsHeapSizeLimit;
          
          const usage = (heapUsed / heapLimit) * 100;
          
          // Update memory pressure state
          if (usage > 85) {
            newMetrics.memoryPressure = 'critical';
            newMetrics.sessionStability = 'unstable';
          } else if (usage > 75) {
            newMetrics.memoryPressure = 'high';
            newMetrics.sessionStability = 'degraded';
          } else if (usage > 60) {
            newMetrics.memoryPressure = 'moderate';
          } else {
            newMetrics.memoryPressure = 'normal';
          }
          
          // Log high memory usage
          if (usage > 75) {
            addLog('warn', 'system', `High memory usage detected: ${usage.toFixed(1)}%`, {
              heapUsed: Math.round(heapUsed / 1024 / 1024) + 'MB',
              heapTotal: Math.round(heapTotal / 1024 / 1024) + 'MB',
              heapLimit: Math.round(heapLimit / 1024 / 1024) + 'MB',
              memoryPressure: newMetrics.memoryPressure
            });
            
            // Trigger emergency GC if available
            if (window.gc && usage > 85) {
              try {
                window.gc();
                addLog('info', 'system', 'Emergency garbage collection triggered');
              } catch (err) {
                addLog('error', 'system', 'Failed to trigger emergency GC', { error: err.message });
              }
            }
          }
        }

        // Check audio context state
        if (audioRef.current) {
          const audio = audioRef.current;
          
          // Audio context state (if available via gain node)
          if (audio.gainNode && audio.gainNode.context) {
            const context = audio.gainNode.context;
            newMetrics.audioContextState = context.state;
            newMetrics.sampleRate = context.sampleRate;
            
            // Estimate buffer size from context
            if (context.baseLatency !== undefined) {
              newMetrics.bufferSize = Math.round(context.baseLatency * context.sampleRate);
            }
            
            // Calculate approximate latency
            if (context.outputLatency !== undefined) {
              newMetrics.audioLatency = Math.round(context.outputLatency * 1000); // Convert to ms
            } else if (context.baseLatency !== undefined) {
              newMetrics.audioLatency = Math.round(context.baseLatency * 1000);
            }
          }

        // Check for potential buffer issues (only if playing and not during track transition)
        const isInTrackTransition = trackTransitionTimeRef.current && (Date.now() - trackTransitionTimeRef.current) < 3000; // 3 second grace period
        
        if (audio.readyState < 2 && !audio.paused && !audio.ended && !isInTrackTransition) {
          if (newMetrics.bufferHealth !== 'poor') {
            newMetrics.bufferHealth = 'poor';
            newMetrics.dropoutCount += 1;
            newMetrics.lastDropoutTime = Date.now();
            addLog('warn', 'audio', 'Potential buffer underrun detected', {
              readyState: audio.readyState,
              paused: audio.paused,
              ended: audio.ended,
              currentTime: audio.currentTime
            });
          }
        } else if (audio.readyState >= 3) {
          newMetrics.bufferHealth = 'good';
        } else {
          newMetrics.bufferHealth = 'fair';
        }
        }

        return newMetrics;
      });
    };

    // Update metrics every 3 seconds in Electron, 2 seconds in web (reduce frequency for stability)
    const isElectron = typeof window !== 'undefined' && window.electronAPI;
    const updateInterval = isElectron ? 3000 : 2000;
    
    metricsIntervalRef.current = setInterval(updateMetrics, updateInterval);
    updateMetrics(); // Initial update

    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [isOpen, addLog, audioRef]);

  // Attach audio element event listeners for diagnostics (only when playing)
  useEffect(() => {
    if (!isOpen || !audioRef.current || !isPlaying) return;

    const audio = audioRef.current;
    const handleStalled = () => addLog('warn', 'audio', 'Audio element stalled');
    const handleError = (e) => addLog('error', 'audio', 'Audio element error', e?.message || e?.type || 'Unknown error');
    const handleSuspend = () => addLog('warn', 'audio', 'Audio element suspended');
    const handleWaiting = () => {
      // Only log if not in track transition
      const isInTrackTransition = trackTransitionTimeRef.current && (Date.now() - trackTransitionTimeRef.current) < 3000;
      if (!isInTrackTransition) {
        addLog('warn', 'audio', 'Audio element waiting for data');
        // Increment dropout counter when waiting for data
        setMetrics(prev => ({
          ...prev,
          dropoutCount: prev.dropoutCount + 1,
          lastDropoutTime: Date.now(),
          bufferHealth: 'poor'
        }));
      }
    };

    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('error', handleError);
    audio.addEventListener('suspend', handleSuspend);
    audio.addEventListener('waiting', handleWaiting);

    return () => {
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('suspend', handleSuspend);
      audio.removeEventListener('waiting', handleWaiting);
    };
  }, [isOpen, audioRef, isPlaying, addLog]);

  // Monitor audio data levels for dropouts (only when playing)
  useEffect(() => {
    if (!isOpen || !analyserRef.current || !dataArrayRef.current || !isPlaying) return;

    let lastAudioLevels = [];
    let normalRange = { min: 100, max: 200 }; // Expected range based on your logs

    const monitorAudioLevels = () => {
      try {
        const analyser = analyserRef.current;
        const dataArray = dataArrayRef.current;
        
        if (analyser && dataArray) {
          analyser.getByteFrequencyData(dataArray);
          
          // Match visualizer sampling strategy - sample across full spectrum like visualizer does
          const samples = [];
          const step = Math.floor(dataArray.length / 8); // Same 8-sample approach as visualizer
          
          for (let i = 0; i < 8; i++) {
            const index = Math.min(i * step, dataArray.length - 1);
            samples.push(dataArray[index]);
          }
          
          const avgLevel = samples.reduce((sum, val) => sum + val, 0) / samples.length;
          const maxLevel = Math.max(...samples);
          
          // Log raw data for comparison with visualizer (debug) - Throttled for Electron
          const isElectron = typeof window !== 'undefined' && window.electronAPI;
          const shouldLog = isElectron ? 
            (Date.now() - (window.lastAudioDebugLog || 0)) > 3000 : // Every 3 seconds in Electron
            avgLevel < 100; // Original condition for web
            
          if (shouldLog && avgLevel < 100) {
            if (isElectron) window.lastAudioDebugLog = Date.now();
            
            addLog('debug', 'audio', 'Raw audio samples for comparison', {
              samples: samples.map(s => Math.round(s)),
              avgLevel: Math.round(avgLevel),
              maxLevel: Math.round(maxLevel),
              dataArrayLength: dataArray.length
            });
          }
          
          // Update normal range based on recent data (adaptive)
          if (maxLevel > 50 && maxLevel < 255) {
            normalRange.min = Math.min(normalRange.min, avgLevel * 0.6);
            normalRange.max = Math.max(normalRange.max, maxLevel * 1.2);
          }
          
          // Check if in track transition - skip dropout detection during transitions
          const isInTrackTransition = trackTransitionTimeRef.current && (Date.now() - trackTransitionTimeRef.current) < 3000; // 3 second grace period
          
          // Detect significant drops - ULTRA SENSITIVE detection based on actual logs (only if not transitioning)
          if (lastAudioLevels.length > 0 && !isInTrackTransition) {
            const lastAvg = lastAudioLevels[lastAudioLevels.length - 1];
            const dropRatio = avgLevel / lastAvg;
            
            // Enhanced primary dropout detection - catches ANY drop below 35 from reasonable levels
            if (avgLevel < 35 && lastAvg > 50) { // More sensitive: catches drops to very low levels
              addLog('error', 'audio', '🚨 SEVERE AUDIO DROPOUT DETECTED', {
                currentLevel: Math.round(avgLevel),
                previousLevel: Math.round(lastAvg),
                dropRatio: Math.round(dropRatio * 100) / 100,
                samples: samples.map(s => Math.round(s)),
                timestamp: new Date().toISOString(),
                severity: 'CRITICAL'
              });
              
              // Update metrics
              setMetrics(prev => ({
                ...prev,
                majorDropoutCount: prev.majorDropoutCount + 1,
                lastMajorDropoutTime: Date.now(),
                bufferHealth: 'poor'
              }));
            }
            // Enhanced secondary detection - catches drops below 45 from moderate levels  
            else if (avgLevel < 45 && lastAvg > 65) { // Catch moderate drops that are still audible
              addLog('error', 'audio', '🚨 MAJOR AUDIO DROPOUT DETECTED', {
                currentLevel: Math.round(avgLevel),
                previousLevel: Math.round(lastAvg),
                dropRatio: Math.round(dropRatio * 100) / 100,
                samples: samples.map(s => Math.round(s)),
                timestamp: new Date().toISOString(),
                severity: 'HIGH'
              });
              
              // Update metrics
              setMetrics(prev => ({
                ...prev,
                majorDropoutCount: prev.majorDropoutCount + 1,
                lastMajorDropoutTime: Date.now(),
                bufferHealth: 'poor'
              }));
            }
            // Enhanced tertiary detection - catches major relative drops (40%+ drop)
            else if (dropRatio < 0.4 && lastAvg > 40) { 
              addLog('error', 'audio', '🚨 RELATIVE AUDIO DROPOUT DETECTED', {
                currentLevel: Math.round(avgLevel),
                previousLevel: Math.round(lastAvg),
                dropRatio: Math.round(dropRatio * 100) / 100,
                samples: samples.map(s => Math.round(s)),
                timestamp: new Date().toISOString(),
                severity: 'HIGH'
              });
              
              // Update metrics
              setMetrics(prev => ({
                ...prev,
                majorDropoutCount: prev.majorDropoutCount + 1,
                lastMajorDropoutTime: Date.now(),
                bufferHealth: 'poor'
              }));
            }
            // Quaternary detection - catches drops below 60 from normal levels
            else if (avgLevel < 60 && lastAvg > 80) {
              addLog('warn', 'audio', '⚠️ Audio level dropped significantly', {
                currentLevel: Math.round(avgLevel),
                previousLevel: Math.round(lastAvg),
                samples: samples.map(s => Math.round(s)),
                timestamp: new Date().toISOString(),
                severity: 'MEDIUM'
              });
            }
            // Quinary detection - catches moderate drops (50%+ drop)
            else if (dropRatio < 0.5 && lastAvg > 60) {
              addLog('info', 'audio', '📉 Audio level drop detected', {
                currentLevel: Math.round(avgLevel),
                previousLevel: Math.round(lastAvg),
                dropRatio: Math.round(dropRatio * 100) / 100,
                samples: samples.map(s => Math.round(s)),
                timestamp: new Date().toISOString(),
                severity: 'LOW'
              });
            }
          }
          
          // Additional detection: Ultra-low audio levels (system-level issues)
          if (avgLevel < 20 && samples.some(s => s > 0)) { // Audio present but extremely quiet
            // BUFFER UNDERRUN INVESTIGATION: Correlate with buffer events
            const bufferCorrelation = correlateWithUltraLowAudio(avgLevel, samples);
            
            addLog('error', 'audio', '🔇 ULTRA-LOW AUDIO LEVEL DETECTED', {
              currentLevel: Math.round(avgLevel),
              samples: samples.map(s => Math.round(s)),
              timestamp: new Date().toISOString(),
              severity: bufferCorrelation.isCorrelated ? 'BUFFER_UNDERRUN' : 'SYSTEM_ISSUE',
              note: bufferCorrelation.isCorrelated 
                ? 'Buffer underrun detected - correlates with buffer events'
                : 'Possible audio driver or system-level audio issue',
              bufferCorrelation: bufferCorrelation.isCorrelated ? {
                correlatedEvents: bufferCorrelation.bufferEvents.map(e => e.type),
                bufferHealth: bufferCorrelation.bufferHealth
              } : undefined
            });
            
            // Enhanced logging for buffer correlation
            if (bufferCorrelation.isCorrelated) {
              audioPerformanceLogger.error('buffer', 'Ultra-low audio CONFIRMED as buffer underrun', {
                audioLevel: avgLevel,
                samples,
                bufferEvents: bufferCorrelation.bufferEvents,
                bufferHealth: bufferCorrelation.bufferHealth,
                timestamp: new Date().toISOString()
              });
            }
          }
          
          // Keep last 5 measurements for comparison
          lastAudioLevels.push(avgLevel);
          if (lastAudioLevels.length > 5) {
            lastAudioLevels.shift();
          }
          
          // Update real-time audio level metrics
          setMetrics(prev => ({
            ...prev,
            audioLevelRange: {
              current: Math.round(avgLevel),
              avg: Math.round(lastAudioLevels.reduce((sum, val) => sum + val, 0) / lastAudioLevels.length),
              min: Math.min(prev.audioLevelRange.min, avgLevel),
              max: Math.max(prev.audioLevelRange.max, avgLevel)
            }
          }));
        }
      } catch (error) {
        // Silent catch to avoid spamming logs with analyser errors
      }
    };

    // Monitor every 100ms for more responsive detection
    const monitorInterval = setInterval(monitorAudioLevels, 100);

    return () => {
      clearInterval(monitorInterval);
    };
  }, [isOpen, analyserRef, dataArrayRef, isPlaying, addLog]);

  // Auto-scroll logs
  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Cleanup when dashboard closes
  useEffect(() => {
    if (!isOpen) {
      // Don't stop buffer monitoring when dashboard closes, just clear the UI state
      setBufferReport(null);
    }
  }, [isOpen]);

  // Emergency audio recovery
  const handleAudioRecovery = useCallback(async () => {
    addLog('info', 'audio', 'Emergency audio recovery initiated');
    
    try {
      if (audioRef.current) {
        const audio = audioRef.current;
        const currentTime = audio.currentTime;
        const wasPlaying = !audio.paused;
        
        // Pause and reset
        audio.pause();
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Resume if was playing
        if (wasPlaying && currentSong) {
          audio.currentTime = currentTime;
          await audio.play();
          addLog('info', 'audio', 'Audio recovery successful');
        }
      }
      
      // Try to resume audio context if suspended
      if (audioRef.current?.gainNode?.context) {
        const context = audioRef.current.gainNode.context;
        if (context.state === 'suspended') {
          await context.resume();
          addLog('info', 'audio', 'Audio context resumed');
        }
      }
    } catch (error) {
      addLog('error', 'audio', 'Audio recovery failed', error.message);
    }
  }, [audioRef, currentSong, addLog]);

  // Copy logs to clipboard
  const copyLogs = useCallback(() => {
    const logText = logs
      .map(log => `[${log.timestamp}] ${log.level.toUpperCase()} [${log.category}] ${log.message}${log.data ? ` | Data: ${JSON.stringify(log.data)}` : ''}`)
      .join('\n');
    
    navigator.clipboard.writeText(logText).then(() => {
      addLog('info', 'system', `Copied ${logs.length} log entries to clipboard`);
    }).catch(() => {
      addLog('error', 'system', 'Failed to copy logs to clipboard');
    });
  }, [logs, addLog]);

  // Download logs as file
  const downloadLogs = useCallback(() => {
    const logText = logs
      .map(log => `[${log.timestamp}] ${log.level.toUpperCase()} [${log.category}] ${log.message}${log.data ? ` | Data: ${JSON.stringify(log.data)}` : ''}`)
      .join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sonic-groove-audio-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    addLog('info', 'system', 'Logs downloaded successfully');
  }, [logs, addLog]);

  // Clear logs
  const clearLogs = useCallback(() => {
    setLogs([]);
    addLog('info', 'system', 'Logs cleared');
  }, [addLog]);

  // Reset audio diagnostics
  const resetDiagnostics = useCallback(() => {
    resetAudioDiagnostics();
    setDiagnosticsReport(null);
    addLog('info', 'diagnostics', 'Audio context diagnostics reset');
  }, [addLog]);

  // Reset buffer monitoring
  const resetBufferMonitoringState = useCallback(() => {
    resetBufferMonitoring();
    setBufferReport(null);
    addLog('info', 'buffer', 'Buffer monitoring statistics reset');
  }, [addLog]);

  // Get status color for metrics
  const getStatusColor = (value, type) => {
    switch (type) {
      case 'bufferHealth':
        return value === 'good' ? 'text-green-400' : value === 'fair' ? 'text-yellow-400' : 'text-red-400';
      case 'audioContextState':
        return value === 'running' ? 'text-green-400' : value === 'suspended' ? 'text-yellow-400' : 'text-red-400';
      case 'cpuUsage':
        return value < 50 ? 'text-green-400' : value < 80 ? 'text-yellow-400' : 'text-red-400';
      case 'memoryUsage':
        return value < 60 ? 'text-green-400' : value < 80 ? 'text-yellow-400' : 'text-red-400';
      default:
        return 'text-white';
    }
  };

  // Format uptime
  const formatUptime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (!isOpen) return null;

  // Main dashboard content as a portal
  return createPortal(
    <div 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw',
        height: '100vh',
        zIndex: 2147483647, // max z-index
        backgroundColor: 'rgba(0, 0, 0, 0.98)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
        border: '4px solid #f00', // debug border
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ padding: '24px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Activity style={{ width: '24px', height: '24px', color: '#60a5fa' }} />
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', margin: 0 }}>Audio Performance Dashboard</h1>
          <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>(Electron Build)</span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            color: 'white',
            padding: '8px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          <X style={{ width: '20px', height: '20px' }} />
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Panel - Metrics */}
        <div style={{ width: '50%', padding: '24px', borderRight: '1px solid rgba(255, 255, 255, 0.2)', overflowY: 'auto' }}>
          {/* Real-time Metrics */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap style={{ width: '20px', height: '20px', color: '#fbbf24' }} />
              Real-time Metrics
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>Audio Latency</div>
                <div style={{ fontSize: '20px', fontFamily: 'monospace', color: 'white' }}>{metrics.audioLatency}ms</div>
              </div>
              
              <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>Buffer Health</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontFamily: 'monospace', 
                  color: metrics.bufferHealth === 'good' ? '#4ade80' : metrics.bufferHealth === 'fair' ? '#fbbf24' : '#f87171'
                }}>
                  {metrics.bufferHealth}
                </div>
              </div>
            </div>
          </div>

          {/* Audio Context Info */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Settings style={{ width: '20px', height: '20px', color: '#60a5fa' }} />
              Audio Context
            </h2>
            
            <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>State:</span>
                <span style={{ 
                  color: metrics.audioContextState === 'running' ? '#4ade80' : metrics.audioContextState === 'suspended' ? '#fbbf24' : '#f87171'
                }}>
                  {metrics.audioContextState}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Sample Rate:</span>
                <span style={{ color: 'white' }}>{metrics.sampleRate} Hz</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Buffer Size:</span>
                <span style={{ color: 'white' }}>{metrics.bufferSize} samples</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Dropouts:</span>
                <span style={{ color: metrics.dropoutCount > 0 ? '#f87171' : '#4ade80' }}>
                  {metrics.dropoutCount}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Major Dropouts:</span>
                <span style={{ color: metrics.majorDropoutCount > 0 ? '#dc2626' : '#4ade80' }}>
                  {metrics.majorDropoutCount}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Uptime:</span>
                <span style={{ color: 'white' }}>{formatUptime(metrics.uptime)}</span>
              </div>
            </div>
          </div>

          {/* Real-time Audio Levels */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity style={{ width: '20px', height: '20px', color: '#10b981' }} />
              Audio Levels (Live)
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>Current Level</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontFamily: 'monospace', 
                  color: metrics.audioLevelRange.current < 50 ? '#f87171' : 
                         metrics.audioLevelRange.current < 100 ? '#fbbf24' : '#4ade80'
                }}>
                  {metrics.audioLevelRange.current}
                </div>
              </div>
              
              <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>Average Level</div>
                <div style={{ fontSize: '20px', fontFamily: 'monospace', color: 'white' }}>
                  {metrics.audioLevelRange.avg}
                </div>
              </div>
            </div>
            
            <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Session Min:</span>
                <span style={{ color: 'white' }}>{metrics.audioLevelRange.min}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Session Max:</span>
                <span style={{ color: 'white' }}>{metrics.audioLevelRange.max}</span>
              </div>
            </div>
          </div>

          {/* Emergency Controls */}
          <div>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RefreshCw style={{ width: '20px', height: '20px', color: '#4ade80' }} />
              Emergency Controls
            </h2>
            
            <button
              onClick={handleAudioRecovery}
              style={{
                width: '100%',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <AlertTriangle style={{ width: '16px', height: '16px' }} />
              Emergency Audio Recovery
            </button>
            
            <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              💡 Logging is {isPlaying ? 'active' : 'paused'} - {isPlaying ? 'dropouts will be detected' : 'resume playback to monitor'}
            </div>
          </div>

          {/* Audio Context Diagnostics */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Search style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
              Audio Context Diagnostics
            </h2>
            
            {diagnosticsReport ? (
              <div style={{ space: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Active Contexts</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: diagnosticsReport.totalContexts === 1 ? '#4ade80' : '#f59e0b' }}>
                      {diagnosticsReport.totalContexts}
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Connections</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#60a5fa' }}>
                      {diagnosticsReport.connectionCount}
                    </div>
                  </div>
                </div>
                
                {diagnosticsReport.potentialIssues && diagnosticsReport.potentialIssues.length > 0 && (
                  <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>⚠️ Issues Detected</div>
                    {diagnosticsReport.potentialIssues.map((issue, index) => (
                      <div key={index} style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>
                        • {issue.description} (Severity: {issue.severity})
                      </div>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={resetDiagnostics}
                  style={{
                    background: 'rgba(245, 158, 11, 0.2)',
                    color: '#f59e0b',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Reset Diagnostics
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>
                Initializing diagnostics...
              </div>
            )}
          </div>

          {/* Buffer Management & Monitoring */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity style={{ width: '20px', height: '20px', color: '#06b6d4' }} />
              Buffer Management & Monitoring
            </h2>
            
            {bufferReport ? (
              <div style={{ space: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Buffer Health</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: bufferReport.bufferHealth.currentBuffer > 5 ? '#4ade80' : '#f59e0b' }}>
                      {bufferReport.bufferHealth.currentBuffer ? `${bufferReport.bufferHealth.currentBuffer.toFixed(1)}s` : 'N/A'}
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Total Underruns</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: bufferReport.statistics.totalUnderruns > 0 ? '#ef4444' : '#4ade80' }}>
                      {bufferReport.statistics.totalUnderruns}
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Ready State</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#60a5fa' }}>
                      {bufferReport.audioElement.readyState}/4
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>Buffer Statistics</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'grid', gap: '4px' }}>
                      <div>Waiting Events: {bufferReport.statistics.totalWaiting}</div>
                      <div>Stalls: {bufferReport.statistics.totalStalls}</div>
                      <div>Suspensions: {bufferReport.statistics.totalSuspensions}</div>
                      <div>Avg Underrun: {bufferReport.statistics.averageUnderrunDuration ? `${bufferReport.statistics.averageUnderrunDuration.toFixed(0)}ms` : 'N/A'}</div>
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>Current Status</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'grid', gap: '4px' }}>
                      <div>Buffered Ranges: {bufferReport.bufferHealth.bufferedRanges?.length || 0}</div>
                      <div>Network State: {bufferReport.audioElement.networkState}/3</div>
                      <div>In Underrun: {bufferReport.underrunDetection.isInUnderrun ? '🔴 Yes' : '🟢 No'}</div>
                      <div>Paused: {bufferReport.audioElement.paused ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </div>
                
                {bufferReport.recentEvents && bufferReport.recentEvents.length > 0 && (
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)', marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>Recent Buffer Events</div>
                    <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {bufferReport.recentEvents.slice(-5).map((event, index) => (
                        <div key={index} style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px', fontFamily: 'monospace' }}>
                          <span style={{ color: event.type === 'waiting' ? '#ef4444' : event.type === 'stalled' ? '#f59e0b' : '#4ade80' }}>
                            {event.type}
                          </span> - {new Date(event.timestamp).toLocaleTimeString()}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={resetBufferMonitoringState}
                  style={{
                    background: 'rgba(6, 182, 212, 0.2)',
                    color: '#06b6d4',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Reset Buffer Stats
                </button>
                
                {/* Buffer Optimization System */}
                {bufferOptimizerStatus && (
                  <div
                    style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '16px',
                      marginTop: '16px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#3b82f6'
                    }}>
                      <Zap size={16} />
                      Buffer Optimization System
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                      gap: '12px' 
                    }}>
                      <div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginBottom: '4px' }}>
                          Status
                        </div>
                        <div style={{ 
                          color: bufferOptimizerStatus.isOptimizing ? '#10b981' : '#ef4444',
                          fontWeight: '600' 
                        }}>
                          {bufferOptimizerStatus.isOptimizing ? '🟢 Active' : '🔴 Inactive'}
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginBottom: '4px' }}>
                          Buffer Ahead
                        </div>
                        <div style={{ 
                          color: bufferOptimizerStatus.bufferAhead >= 4 ? '#10b981' : 
                                bufferOptimizerStatus.bufferAhead >= 2 ? '#f59e0b' : '#ef4444',
                          fontWeight: '600' 
                        }}>
                          {bufferOptimizerStatus.bufferAhead ? `${bufferOptimizerStatus.bufferAhead.toFixed(1)}s` : 'N/A'}
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginBottom: '4px' }}>
                          Preloaded Tracks
                        </div>
                        <div style={{ color: '#3b82f6', fontWeight: '600' }}>
                          {bufferOptimizerStatus.preloadedTracks || 0}
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginBottom: '4px' }}>
                          Recovery Attempts
                        </div>
                        <div style={{ 
                          color: bufferOptimizerStatus.recoveryAttempts === 0 ? '#10b981' : 
                                bufferOptimizerStatus.recoveryAttempts <= 2 ? '#f59e0b' : '#ef4444',
                          fontWeight: '600' 
                        }}>
                          {bufferOptimizerStatus.recoveryAttempts || 0} / 3
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginBottom: '4px' }}>
                          Buffer Underruns
                        </div>
                        <div style={{ 
                          color: (bufferReport?.statistics?.totalUnderruns || 0) === 0 ? '#10b981' : '#ef4444',
                          fontWeight: '600' 
                        }}>
                          {bufferReport?.statistics?.totalUnderruns || 0}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                      <div>Ready State: {bufferOptimizerStatus.readyState || 'N/A'}</div>
                      {bufferReport && (
                        <div>Buffer Health: {bufferReport.bufferHealth?.currentBuffer ? `${bufferReport.bufferHealth.currentBuffer.toFixed(1)}s` : 'N/A'}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>
                Buffer monitoring not active (will start when song loads)
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Logs */}
        <div style={{ width: '50%', padding: '24px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity style={{ width: '20px', height: '20px', color: '#4ade80' }} />
              Performance Log ({logs.length}/500)
            </h2>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setAutoScroll(!autoScroll)}
                style={{
                  background: autoScroll ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Auto-scroll {autoScroll ? 'ON' : 'OFF'}
              </button>
              <button
                onClick={copyLogs}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: 'none',
                  padding: '6px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <Copy style={{ width: '16px', height: '16px' }} />
              </button>
              <button
                onClick={downloadLogs}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: 'none',
                  padding: '6px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <Download style={{ width: '16px', height: '16px' }} />
              </button>
              <button
                onClick={clearLogs}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: 'none',
                  padding: '6px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <Trash2 style={{ width: '16px', height: '16px' }} />
              </button>
            </div>
          </div>

          {/* Logs Container */}
          <div
            ref={logsContainerRef}
            style={{
              flex: 1,
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              padding: '16px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {logs.length === 0 ? (
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', padding: '32px 0' }}>
                No logs yet. Audio events will appear here in real-time.
              </div>
            ) : (
              logs.map(log => (
                <div
                  key={log.id}
                  style={{
                    marginBottom: '4px',
                    color: log.level === 'error' ? '#f87171' :
                           log.level === 'warn' ? '#fbbf24' :
                           log.level === 'debug' ? '#9ca3af' :
                           'white'
                  }}
                >
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    [{new Date(log.timestamp).toLocaleTimeString()}]
                  </span>
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 4px',
                    borderRadius: '2px',
                    fontSize: '10px',
                    background: log.level === 'error' ? 'rgba(248, 113, 113, 0.2)' :
                               log.level === 'warn' ? 'rgba(251, 191, 36, 0.2)' :
                               log.level === 'debug' ? 'rgba(156, 163, 175, 0.2)' :
                               'rgba(96, 165, 250, 0.2)'
                  }}>
                    {log.level.toUpperCase()}
                  </span>
                  <span style={{ marginLeft: '8px', color: 'rgba(255, 255, 255, 0.8)' }}>[{log.category}]</span>
                  <span style={{ marginLeft: '8px' }}>{log.message}</span>
                  {log.data && (
                    <div style={{ marginLeft: '32px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>
                      {typeof log.data === 'string' ? log.data : JSON.stringify(log.data, null, 2)}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          
          <div style={{ marginTop: '16px', fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)' }}>
            💡 Tip: Logs are limited to 500 entries and can be copied or downloaded for analysis.
            The dashboard monitors audio health and provides emergency recovery options.
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
